import type { Texture, Color, MeshStandardMaterialParameters } from "three";

export interface BakedColorTexture {
  texture: Texture;
  color?: Color; // In case of a textured material, can be undefined.
  worldWidth: number;
  worldHeight: number;
  rotation: number;
  loadedPromise: Promise<void>;
}

export interface DesignMaterialMigration {
  description?: string;
  maxSchemaVersion: string;
  applyOnLayerConfigs: boolean;
  apply(res: any): any;
}

export const DmLayerSrcIDs = ["none", "texture", "SDFDepthMask", "SDFRandom"] as const;
export const DmUvTransformIDs = ["normalMap", "layer"] as const;
export const DmMaskUvTransformIDs = ["normalMap", "mask"] as const;
export const DmMaskIDs = ["none", "SDFMask", "Mask0", "Mask1", "Mask2", "Mask3", "normalMap"] as const;
export const DmSlopeAxises = ["X", "Z"] as const;
export const DmRandomTilesModes = ["None", "RandomSplit"] as const;
export const DmRoundSidesModes = ["none", "roundX", "roundY", "roundZ", "roundSides"] as const;
export const DmSplitDirections = ["None", "X", "Y", "LongerAxis", "Random"] as const;
export const DmProceduralHatchMaterialIds = ["Auto"] as const;
export const DmTransparencyModes = [
  "opaque",
  "sdfTransparent",
  "metalScreen",
  "approximateClearGlass",
  "alphaTransparentDoNotUse",
  "experimentalTransmissiveDoNotUse",
  "frostedGlass",
] as const;

export type DmLayerSrcID = (typeof DmLayerSrcIDs)[number];
export type DmUvTransformID = (typeof DmUvTransformIDs)[number];
export type DmMaskUvTransformID = (typeof DmMaskUvTransformIDs)[number];
export type DmMaskID = (typeof DmMaskIDs)[number];
export type DmSlopeAxis = (typeof DmSlopeAxises)[number];
export type DmRandomTilesMode = (typeof DmRandomTilesModes)[number];
export type DmRoundSidesMode = (typeof DmRoundSidesModes)[number];
export type DmProceduralHatchMaterialId = (typeof DmProceduralHatchMaterialIds)[number];
export type DmTransparencyMode = (typeof DmTransparencyModes)[number];

export interface DesignMaterialParams
  extends Required<
    Pick<MeshStandardMaterialParameters, "color" | "roughness" | "metalness" | "side" | "shadowSide" | "opacity">
  > {
  version: string;
  type: "material";

  // MeshPhysicalMaterialParameters allows multiple types of color values, but we only allow hex strings.
  color: string;

  transparency: DmTransparencyMode;

  allowUsingTemplateOnItsOwn: boolean;
  compatibleLayerConfigLibraries: string[];
  compatibleColorLibraries: string[];

  rotateAllLayers: number;

  clearcoat: number;
  clearcoatRoughness: number;
  reflectivity: number;
  transmission: number;

  useLegacyGamma: boolean;

  useEnvMap: boolean;
  useSpecularColor: boolean;
  specularColor: string;
  envMapIntensity: number;
  layerHeightFilterWidth: number;
  layerHeightAoStrength: number;
  sdf: SdfParams;
  layers: LayerParams[];
  masks: MaskParams[];
  normalMapEnabled: boolean;
  normalMapName: string;
  normalMapStrength: number;
  normalMapScaleU: number;
  normalMapScaleV: number;
  normalMapTranslateU: number;
  normalMapTranslateV: number;
  normalMapRotation: number;
  randomizeShader: boolean;
  randomizeShaderSeed: number;
  disableSSAO: boolean;
  mainMaskTexName: string;
  heightMapEnabled: boolean;
  heightMapName: string;
  heightMapScaleU: number;
  heightMapScaleV: number;
  heightMapDepth: number;
  heightMapRotation: number;
  hatchMaterialId: string | DmProceduralHatchMaterialId;
  hatchMaterialIgnoreVaryHeight: boolean;
  hatchMaterialIgnoreBevels: boolean; // Optimization: this can be used to simplify hatch materials so that the linework can be rendered faster.
  thumbnailDiagonalCamera: boolean;
}

export const DESIGN_MATERIAL_VERSION = "0.4.52";

export interface DesignMaterialParamsCurrentVersion extends DesignMaterialParams {
  version: typeof DESIGN_MATERIAL_VERSION;
}

/**
 * The preprocessed parameters are distilled down to the minimal set of data that's relevant for rendering.
 * Disabled parts are omitted, and some data is simplified so that there are no conflicting values and need for further processing is minimized.
 */
export interface DesignMaterialParamsPreprocessed
  extends Omit<DesignMaterialParams, "rotateAllLayers" | "layers" | "sdf"> {
  preprocessed: true; // Added so that plain DesignMaterialParams can't be passed to functions that expect preprocessed parameters.
  layers: LayerParamsPreprocessed[];
  sdf: SdfParamsPreprocessed;
}

export interface LayerParamsPreprocessed extends Omit<LayerParams, "enabled"> {
  originalIndex: number;
}

export interface SdfCommonParamsPreprocessed extends Omit<SdfCommonParams, "rotateAllSdfPatterns"> {}

export interface SdfParamsPreprocessed extends Omit<SdfParams, "patterns" | "common"> {
  common: SdfCommonParamsPreprocessed;
  patterns: SdfPatternPreprocessed[];
  preprocessed: true;
}

export type SdfPatternPreprocessed = Omit<SdfPattern, "enabled">;

/**
 * This version of the SDF pattern data is used in the SDF shader code. It's not relevant for parallax map based rendering.
 */
export interface ShaderSdfPattern extends SdfPatternPreprocessed {
  enabledForShader: boolean;
}

// Layer configurations can be used to override a subset of design material params.
export interface LayerConfigParams {
  version: string;
  type: "layerConfig";

  color: string;
  useLegacyGamma: boolean;
  layers: LayerParams[];
  masks: MaskParams[];
}

export interface UVTransformParams {
  scaleU: number;
  scaleV: number;
  translateU: number;
  translateV: number;
  rotate: number;
}

export const DmBlendModes = [
  "alpha",
  "add",
  "subtract",
  "difference",
  "multiply",
  "screen",
  "lighten",
  "darken",
  "legacy-screen",
] as const;

export type DmBlendMode = (typeof DmBlendModes)[number];

export interface LayerParams extends UVTransformParams {
  enabled: boolean;
  srcID: DmLayerSrcID;
  uvTransformID: DmUvTransformID;
  textureName: string;
  blendMode: DmBlendMode;
  strength: number;
  invertInput: boolean;
  maskID: DmMaskID;
  maskUvTransformID: DmMaskUvTransformID;
  sdfMaskRandomness: number;
  sdfMaskRandomAverage: number;
  sdfMaskPatterns: number;
  sdfMaskDepthOverlay: number;
  invertMask: boolean;
  enableMaskThreshold: boolean;
  maskThreshold: number;
  colorCount: number;
  color0: string;
  color1: string;
  color2: string;
  color3: string;
  useBaseColorAsColor0: boolean;
  useBaseColorAsColor1: boolean;
  useBaseColorAsColor2: boolean;
  useBaseColorAsColor3: boolean;
  color0Pos: number;
  color1Pos: number;
  color2Pos: number;
  color3Pos: number;
  colorInfluence: number;
  roughnessInfluence: number;
  metalnessInfluence: number;
  useForHeight: boolean;
  heightInfluence: number;
  randomUvOffsetStrength: number;
  randomMaskUniqueness: number;
  maskEdgeWearOverlay: number;
}

export interface MaskParams extends UVTransformParams {
  textureName: string;
  strength: number;
  contrast: number;
}

export interface SdfParams {
  common: SdfCommonParams;

  randomTiles: SdfRandomTilesParams;

  distortions: SdfDistortion[];

  patterns: SdfPattern[];
}

export interface SdfCommonParams {
  maskOnly: boolean;
  autoGenerateHatchIfMaskOnly: boolean;

  useShadows: boolean;
  useAO: boolean;
  randomMaskSeed: number;

  depth: number;
  aoStrength: number;
  aoTolerance: number;
  edgeWearStrength: number;
  edgeWearSpread: number;

  thumbnailAoStrength: number;
  thumbnailAoTolerance: number;

  slopeAxis: DmSlopeAxis;

  randomTilesMode: (typeof DmRandomTilesModes)[number];

  rotateAllSdfPatterns: number;
}

export interface SdfRandomTilesParams {
  tileVariability: number;
  minTileVariability: number; // relative to tileVariability
  tileSplitDirection: (typeof DmSplitDirections)[number];
  tileSplitProbability: number;

  alwaysSplitTilesAboveWidth: number;
  alwaysSplitTilesAboveHeight: number;
  minTileHeight: number;
  minTileWidth: number;
}

export interface SdfDistortion {
  enabled: boolean;
  periodX: number;
  periodY: number;
  strengthX: number;
  strengthY: number;
  strengthZ: number;
  randomness: number;
}

export interface SdfCellInfo {
  xSize: number;
  ySize: number;
  zSize: number;
  slope: number; //in degrees
  rotation: number; //in degrees
  cutXfromY: boolean;
  cutXfromYScale: number;
  useBevels: boolean;
  bevelDistanceX: number;
  bevelDistanceY: number;
  bevelDistanceZ: number;
  roundSidesMode: DmRoundSidesMode;
  roundStrength: number;
}

export interface SdfPattern extends SdfCellInfo {
  enabled: boolean;
  varyHeight: boolean;
  varyHeightScale: number;
  xOffset: number;
  yOffset: number;
  xDistance: number;
  yDistance: number;
  continuousX: boolean;
  continuousY: boolean;
  maskRandomSeedIndex: number;
}

export interface UsedLayerFeatures {
  useSDFMask: boolean;
  useSDFRandom: boolean;
  hasHeightLayers: boolean;
  useSDFMaskOrRandom: boolean;
}

export type ColorLibraryParams = { [name: string]: string }; // Keys are names, values are colors.
export type LayerConfigLibraryParams = string[]; // List of names.
